import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import ContactModal from '../components/common/ContactModal';
import { ExternalLink, Star, MapPin, Check, ArrowRight, Users, Camera, Music, Utensils, Flower } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  photographe: { label: 'Photographe', icon: Camera, color: '#F4A7B2' },
  dj: { label: 'DJ / Musique', icon: Music, color: '#5DBCBF' },
  traiteur: { label: 'Traiteur', icon: Utensils, color: '#D4AF37' },
  fleuriste: { label: 'Fleuriste', icon: Flower, color: '#1F9D7A' },
  default: { label: 'Prestataire', icon: Users, color: '#C58B19' },
};

const ROLES_COMING = [
  { emoji: '🎧', label: 'DJ' },
  { emoji: '🍽️', label: 'Traiteur' },
  { emoji: '💐', label: 'Fleuriste' },
  { emoji: '🎬', label: 'Vidéaste' },
  { emoji: '💄', label: 'Maquillage' },
  { emoji: '🚚', label: 'Food truck' },
  { emoji: '🎂', label: 'Pâtissier' },
  { emoji: '🌿', label: 'Décorateur' },
];

export default function PrestataireRecommandesPage() {
  const [prestataires, setPrestataires] = useState([]);
  const [filtre, setFiltre] = useState('all');
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSujet, setContactSujet] = useState('');

  useEffect(() => {
    fetch(`${BACKEND}/api/prestataires-recommandes`)
      .then(r => r.json())
      .then(d => setPrestataires(d.prestataires || []))
      .catch(() => {});
  }, []);

  const filtered = filtre === 'all' ? prestataires : prestataires.filter(p => p.role === filtre);
  const roles = [...new Set(prestataires.map(p => p.role))];

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HERO */}
      <section className="section-py" style={{ background: 'linear-gradient(135deg, #0B1E20 0%, #1a3a3c 100%)' }}>
        <div className="container-ose text-center">
          <span className="inline-block px-4 py-2 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>
            🤝 Réseau de confiance
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-4"
            style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            Prestataires recommandés<br />
            <span style={{ color: '#D4AF37' }}>par Show Événement</span>
          </h1>
          <p className="text-sm max-w-xl mx-auto mb-6"
            style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.75' }}>
            Des professionnels que nous connaissons, que nous apprécions et en qui nous avons confiance.
            Des partenaires sélectionnés pour leur sérieux, leur talent et leur connaissance du terrain local.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[['✅', 'Vérifiés personnellement'], ['📍', 'Lot, Aveyron, Cantal'], ['⭐', 'Qualité garantie']].map(([icon, label]) => (
              <div key={label} className="flex items-center gap-2 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>
                {icon} {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FILTRES */}
      {roles.length > 1 && (
        <div style={{ background: '#FEFAF5', borderBottom: '1px solid var(--border)' }}>
          <div className="container-ose py-3 flex gap-2 overflow-x-auto">
            <button onClick={() => setFiltre('all')}
              className="px-4 py-2 rounded-xl text-xs font-semibold flex-shrink-0"
              style={{ background: filtre === 'all' ? '#D4AF37' : 'transparent', color: filtre === 'all' ? '#0B1E20' : 'var(--text-muted)', border: filtre === 'all' ? 'none' : '1px solid var(--border)', cursor: 'pointer' }}>
              Tous ({prestataires.length})
            </button>
            {roles.map(r => {
              const info = ROLE_LABELS[r] || ROLE_LABELS.default;
              return (
                <button key={r} onClick={() => setFiltre(r)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold flex-shrink-0"
                  style={{ background: filtre === r ? info.color : 'transparent', color: filtre === r ? '#0B1E20' : 'var(--text-muted)', border: filtre === r ? 'none' : '1px solid var(--border)', cursor: 'pointer' }}>
                  {info.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* LISTE */}
      <section className="section-py">
        <div className="container-ose">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <p style={{ color: 'var(--text-muted)' }}>Chargement…</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filtered.map(p => {
                const roleInfo = ROLE_LABELS[p.role] || ROLE_LABELS.default;
                const RoleIcon = roleInfo.icon;
                return (
                  <div key={p.id} className="card-premium overflow-hidden">
                    <div className="grid md:grid-cols-3 gap-0">
                      {/* Image */}
                      <div className="relative" style={{ minHeight: '280px' }}>
                        <img src={p.image} alt={p.nom}
                          className="w-full h-full object-cover" style={{ minHeight: '280px' }} loading="lazy" />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(11,30,32,0.6) 100%)' }} />
                        {p.badge && (
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1.5 rounded-full text-xs font-bold"
                              style={{ background: p.badge_color || '#D4AF37', color: '#0B1E20' }}>
                              ⭐ {p.badge}
                            </span>
                          </div>
                        )}
                        <div className="absolute bottom-4 left-4">
                          <span className="px-3 py-1.5 rounded-xl text-xs font-bold"
                            style={{ background: roleInfo.color, color: '#0B1E20' }}>
                            <RoleIcon size={11} className="inline mr-1" />{roleInfo.label}
                          </span>
                        </div>
                      </div>

                      {/* Info principale */}
                      <div className="p-6">
                        <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
                          {p.nom}
                        </h2>
                        <p className="text-sm font-semibold mb-3" style={{ color: roleInfo.color }}>
                          {p.tagline}
                        </p>
                        <p className="text-sm mb-4" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
                          {p.description}
                        </p>

                        {p.style && (
                          <div className="mb-3">
                            <p className="text-xs font-bold mb-1" style={{ color: 'var(--ink)' }}>Style :</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.style}</p>
                          </div>
                        )}

                        {p.zone && (
                          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                            <MapPin size={12} style={{ color: roleInfo.color }} />
                            {p.zone}
                          </div>
                        )}

                        {p.events_couverts?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {p.events_couverts.map((e, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ background: `${roleInfo.color}18`, color: roleInfo.color }}>
                                {e}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="p-6 flex flex-col gap-3"
                        style={{ background: 'rgba(11,30,32,0.02)', borderLeft: '1px solid var(--border)' }}>
                        <div className="p-4 rounded-2xl mb-2"
                          style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
                          <p className="text-xs font-bold mb-2" style={{ color: '#C58B19' }}>
                            💛 Pourquoi recommandé
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                            {p.pourquoi_recommande}
                          </p>
                        </div>

                        {p.verified && (
                          <div className="flex items-center gap-2 text-xs" style={{ color: '#1F9D7A' }}>
                            <Check size={13} />
                            Professionnel vérifié par Show Événement
                          </div>
                        )}

                        {p.site && (
                          <a href={p.site} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold"
                            style={{ background: '#0B1E20', color: '#D4AF37', textDecoration: 'none' }}>
                            <ExternalLink size={14} /> Voir le site
                          </a>
                        )}

                        <button
                          onClick={() => { setContactSujet(`Demande de mise en relation — ${p.nom}`); setContactOpen(true); }}
                          className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold"
                          style={{ background: 'transparent', border: '1.5px solid var(--border)', color: 'var(--ink)', cursor: 'pointer' }}>
                          Demander une mise en relation
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Prochains prestataires */}
          <div className="mt-10 p-6 rounded-3xl text-center"
            style={{ background: '#0B1E20', border: '1px solid rgba(212,175,55,0.2)' }}>
            <h3 className="font-display text-lg font-bold mb-2" style={{ color: '#FFFFFF' }}>
              D'autres prestataires arrivent bientôt
            </h3>
            <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Nous sélectionnons soigneusement chaque professionnel. En attendant :
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {ROLES_COMING.map(r => (
                <span key={r.label} className="px-3 py-1.5 rounded-xl text-xs font-medium"
                  style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  {r.emoji} {r.label}
                </span>
              ))}
            </div>
            <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Vous êtes un professionnel de l'événementiel dans le Lot, l'Aveyron ou le Cantal ?
            </p>
            <button
              onClick={() => { setContactSujet('Demande de référencement prestataire'); setContactOpen(true); }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold"
              style={{ background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer' }}>
              Proposer ma candidature
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        sujetPrefill={contactSujet}
        sourcePage="Page prestataires recommandés"
      />
    </div>
  );
}
